import React, { useEffect, useRef, useState } from 'react';
import { Check, Sparkles, ArrowRight } from 'lucide-react';

const PricingSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const plans = [
    {
      name: "Essential",
      subtitle: "For emerging authors",
      price: "799",
      description: "Perfect for authors with one book looking to establish a professional online presence.",
      features: [
        "Professional single-page website",
        "Author biography & book showcase",
        "Purchase links integration",
        "First chapter HTML preview",
        "Responsive design",
        "Social media integration",
        "Basic SEO optimization"
      ],
      addOns: [
        "Additional books: +$150 each",
        "Hosting & maintenance: +$30/month"
      ],
      popular: false,
      cta: "Get Started"
    },
    {
      name: "Professional",
      subtitle: "Most Popular",
      price: "1,499",
      description: "Ideal for established authors with multiple books and a growing readership.",
      features: [
        "Multi-page website with blog",
        "Up to 3 dedicated book pages",
        "3 chapter HTML previews",
        "Members-only content section",
        "Newsletter integration",
        "Enhanced SEO strategy",
        "Google Analytics setup",
        "3 hours of personal training"
      ],
      addOns: [
        "Additional books: +$100 each",
        "Advanced membership: +$500",
        "Hosting & maintenance: +$50/month"
      ],
      popular: true,
      cta: "Choose Professional"
    },
    {
      name: "Premium",
      subtitle: "Complete solution",
      price: "2,999",
      description: "The ultimate platform for professional authors seeking maximum impact and control.",
      features: [
        "Fully custom design",
        "Up to 6 book pages",
        "Unlimited chapter previews",
        "Advanced members area",
        "E-commerce integration",
        "Advanced SEO strategy",
        "Social media feed integration",
        "1 year maintenance included",
        "Priority support"
      ],
      addOns: [
        "Additional books: +$50 each",
        "Marketing materials: +$300",
        "Extended maintenance: +$75/month"
      ],
      popular: false,
      cta: "Go Premium"
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 bg-cream-100 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-ink-900/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
      </div>

      {/* Decorative Lines */}
      <div className="absolute top-24 left-0 w-32 h-px bg-gradient-to-r from-gold-400 to-transparent" />
      <div className="absolute bottom-24 right-0 w-32 h-px bg-gradient-to-l from-gold-400 to-transparent" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold-500" />
            <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
              Investment
            </span>
            <span className="w-8 h-px bg-gold-500" />
          </div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
            Packages Crafted for
            <span className="block mt-2 text-gradient-gold">Every Author's Journey</span>
          </h2>

          <p className="font-body text-lg text-ink-600 leading-relaxed">
            Transparent pricing with no hidden fees. Choose the foundation that fits your vision.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              } ${plan.popular ? 'lg:-mt-4 lg:mb-4' : ''}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <div className="flex items-center gap-2 px-4 py-2 bg-ink-900 text-gold-400 text-sm font-sans tracking-wide rounded-full shadow-luxury">
                    <Sparkles className="w-4 h-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}

              {/* Card */}
              <div className={`relative h-full flex flex-col p-8 lg:p-10 transition-all duration-500 ${
                plan.popular
                  ? 'bg-ink-900 text-cream-50 shadow-luxury-lg border border-gold-500/30'
                  : 'bg-cream-50 text-ink-900 border border-cream-300 hover:border-gold-400/50 hover:shadow-luxury'
              }`}>
                {/* Gold Corner */}
                {plan.popular && (
                  <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-gold-500/20 to-transparent transform rotate-45 translate-x-16 -translate-y-16" />
                  </div>
                )}

                {/* Header */}
                <div className="mb-8">
                  <div className={`text-sm font-sans tracking-wide mb-2 ${plan.popular ? 'text-gold-400' : 'text-gold-600'}`}>
                    {plan.subtitle}
                  </div>
                  <h3 className="font-display text-3xl font-bold mb-4">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-2xl ${plan.popular ? 'text-cream-300' : 'text-ink-500'}`}>$</span>
                    <span className="font-display text-5xl font-bold">{plan.price}</span>
                  </div>
                  <p className={`mt-4 font-body text-base leading-relaxed ${plan.popular ? 'text-cream-300' : 'text-ink-600'}`}>
                    {plan.description}
                  </p>
                </div>

                {/* Divider */}
                <div className={`h-px mb-8 ${plan.popular ? 'bg-ink-700' : 'bg-cream-300'}`} />

                {/* Features */}
                <div className="flex-1 space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                        plan.popular ? 'bg-gold-500/20 text-gold-400' : 'bg-gold-100 text-gold-600'
                      }`}>
                        <Check className="w-3 h-3" />
                      </div>
                      <span className={`font-body ${plan.popular ? 'text-cream-200' : 'text-ink-700'}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Add-ons */}
                <div className={`text-sm space-y-2 mb-8 p-4 rounded-lg ${
                  plan.popular ? 'bg-ink-800/50' : 'bg-cream-100'
                }`}>
                  <div className={`font-sans font-medium mb-2 ${plan.popular ? 'text-cream-200' : 'text-ink-700'}`}>
                    Optional add-ons:
                  </div>
                  {plan.addOns.map((addon, i) => (
                    <div key={i} className={plan.popular ? 'text-cream-400' : 'text-ink-500'}>
                      {addon}
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={scrollToContact}
                  className={`w-full flex items-center justify-center gap-2 py-4 font-sans font-medium tracking-wide transition-all duration-300 group ${
                    plan.popular
                      ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-ink-900 hover:from-gold-400 hover:to-gold-500 shadow-gold'
                      : 'bg-ink-900 text-cream-50 hover:bg-ink-800'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className={`text-center mt-12 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-ink-500 font-body">
            Need something custom? <a href="/contact" className="text-gold-600 hover:text-gold-700 font-medium animated-underline">Let's talk</a> about your specific requirements.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
